import { useEffect, useRef } from 'react';

import useExpiringRawLogUrl from '../../../hooks/useExpiringRawLogUrl';
import useFetchLog, { FetchLogsProps } from './useFetchLog';

const useFetchArchivedLog = (
  appSlug: string,
  buildSlug: string,
  isArchived: boolean,
  isFirstChunkLoaded: boolean,
  publicAccess: boolean | undefined,
  requestStartOfLog: boolean,
  setArchivedLog: (log: string) => void,
  setFirstChunkLoaded: (isFirstChunkLoaded: boolean) => void,
) => {
  const expiringRawLogUrl = useExpiringRawLogUrl(appSlug, buildSlug, false, publicAccess || false);
  const isArchivedLogRequested = useRef<boolean>(false);

  useEffect(() => {
    if (!requestStartOfLog || !isArchived || isFirstChunkLoaded || isArchivedLogRequested.current) {
      return;
    }

    const fetchArchivedLog = async (): Promise<void> => {
      isArchivedLogRequested.current = true;
      const { data: url } = await expiringRawLogUrl.refetch();
      if (url) {
        const file = await fetch(url);
        const text = await file.text();
        setArchivedLog(text);
        setFirstChunkLoaded(true);
      }
    };

    fetchArchivedLog();
  }, [
    isArchived,
    isFirstChunkLoaded,
    requestStartOfLog,
    setArchivedLog,
    setFirstChunkLoaded,
    expiringRawLogUrl,
    isArchivedLogRequested,
  ]);
};

const useFetchNotArchivedLog = (
  fetchPage: () => void,
  isArchived: boolean,
  isFirstChunkLoaded: boolean,
  isFetching: boolean,
  requestStartOfLog: boolean,
) => {
  useEffect(() => {
    if (!requestStartOfLog || isArchived || isFirstChunkLoaded) {
      return;
    }

    fetchPage();
  }, [fetchPage, isArchived, isFirstChunkLoaded, isFetching, requestStartOfLog]);
};

const useFetchPrevLogs = ({ buildData, logData }: FetchLogsProps): { errorStatus: number | undefined } => {
  const { requestStartOfLog } = buildData;
  const { errorStatus, isArchived, isFetching, fetchPage } = useFetchLog(
    {
      buildData,
      isEnabled: requestStartOfLog,
      logData,
    },
    'prevPage',
  );

  const { isFirstChunkLoaded, setFirstChunkLoaded, setArchivedLog } = logData;

  useFetchArchivedLog(
    buildData.appSlug,
    buildData.buildSlug,
    isArchived,
    isFirstChunkLoaded,
    buildData.publicAccess,
    requestStartOfLog,
    setArchivedLog,
    setFirstChunkLoaded,
  );

  useFetchNotArchivedLog(fetchPage, isArchived, isFirstChunkLoaded, isFetching, requestStartOfLog);

  return { errorStatus };
};

export default useFetchPrevLogs;
